import { useState } from "react";
import { formatCurrency, isValidSolanaAddress } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2Icon, AlertCircleIcon } from "lucide-react";
import { toast } from "sonner";
import { useRequestPayout } from "../hooks/use-affiliate-data";
import { useWallet } from "@solana/wallet-adapter-react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";

// Site's green color - brand color for positive actions
const SITE_GREEN = "#2ebd85";

type AffiliateRequestPayoutModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
};

const MINIMUM_PAYOUT = 100; // $100 minimum

export const AffiliateRequestPayoutModal = ({
  open,
  onOpenChange,
  availableBalance,
}: AffiliateRequestPayoutModalProps) => {
  const { publicKey } = useWallet();
  const { authMethod } = useUnifiedAuth();
  const requestPayout = useRequestPayout();

  const [amount, setAmount] = useState("");
  const [customAddress, setCustomAddress] = useState("");
  const [useCustomAddress, setUseCustomAddress] = useState(false);

  const connectedAddress = publicKey?.toBase58();
  const payoutAddress = useCustomAddress ? customAddress : connectedAddress;

  const isValidAmount = () => {
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount >= MINIMUM_PAYOUT && numAmount <= availableBalance;
  };

  const isValidAddress = () => {
    if (!payoutAddress) return false;
    return isValidSolanaAddress(payoutAddress);
  };

  const handleSubmit = async () => {
    if (!isValidAmount() || !isValidAddress() || !payoutAddress) return;

    try {
      await requestPayout.mutateAsync({
        amount: parseFloat(amount),
        address: payoutAddress,
      });
      toast.success("Payout request submitted! You'll be notified when it's processed.");
      onOpenChange(false);
      setAmount("");
      setCustomAddress("");
    } catch (_error) {
      toast.error("Failed to submit payout request. Please try again.");
    }
  };

  const handleMaxClick = () => {
    setAmount(availableBalance.toString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Payout</DialogTitle>
          <DialogDescription>
            Request a payout of your affiliate earnings to your Solana wallet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Available Balance */}
          <div 
            className="rounded-lg p-4 border"
            style={{ 
              backgroundColor: `${SITE_GREEN}20`,
              borderColor: `${SITE_GREEN}40`
            }}
          >
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
              Available Balance
            </p>
            <p className="text-xl font-semibold">
              ${formatCurrency(availableBalance, 2)}
            </p>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Payout Amount</label>
              <button 
                type="button"
                className="text-xs font-medium hover:opacity-80 cursor-pointer transition-opacity"
                style={{ color: SITE_GREEN }}
                onClick={handleMaxClick}
              >
                Max
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7 text-lg border-transparent hover:border-white/40 focus-visible:border-white/70 focus-visible:ring-0"
                min={MINIMUM_PAYOUT}
                max={availableBalance}
                step="0.01"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Minimum payout: ${MINIMUM_PAYOUT}
            </p>
          </div>

          {/* Wallet Address */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Payout Address</label>
            
            {authMethod === "wallet" && connectedAddress && !useCustomAddress ? (
              <>
                <div className="bg-muted/50 border rounded-lg p-3 text-xs break-all text-muted-foreground">
                  {connectedAddress}
                </div>
                <button
                  type="button"
                  onClick={() => setUseCustomAddress(true)}
                  className="text-xs hover:underline"
                  style={{ color: SITE_GREEN }}
                >
                  Use a different address
                </button>
              </>
            ) : (
              <>
                <Input
                  placeholder="Enter Solana wallet address"
                  value={customAddress}
                  onChange={(e) => setCustomAddress(e.target.value)}
                  className="text-xs border-transparent hover:border-white/40 focus-visible:border-white/70 focus-visible:ring-0"
                />
                {customAddress && !isValidSolanaAddress(customAddress) && (
                  <p className="text-xs text-rose-500 flex items-center gap-1">
                    <AlertCircleIcon className="size-3" />
                    Please enter a valid Solana address
                  </p>
                )}
                {authMethod === "wallet" && connectedAddress && (
                  <button
                    type="button"
                    onClick={() => {
                      setUseCustomAddress(false);
                      setCustomAddress("");
                    }}
                    className="text-xs hover:underline"
                    style={{ color: SITE_GREEN }}
                  >
                    Use connected wallet
                  </button>
                )}
              </>
            )}
          </div>

          {/* Info Box */}
          <div 
            className="rounded-lg p-3 border"
            style={{ 
              backgroundColor: `${SITE_GREEN}12`,
              borderColor: `${SITE_GREEN}30`
            }}
          >
            <p 
              className="text-xs"
              style={{ color: SITE_GREEN }}
            >
              Payouts are reviewed and processed manually. You'll receive a notification
              when your payout is approved and sent.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={requestPayout.isPending}
            className="flex-1 h-11"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValidAmount() || !isValidAddress() || requestPayout.isPending}
            className="flex-1 h-11 text-white"
            style={{ backgroundColor: SITE_GREEN }}
          >
            {requestPayout.isPending ? (
              <>
                <Loader2Icon className="size-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              `Request $${amount || "0"}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
